import React, { useEffect, useMemo } from 'react'
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Typography,
} from 'antd'
import { Controller, useForm } from 'react-hook-form'
import { useUnit } from 'effector-react'
import { Link, useNavigate } from 'react-router-dom'
import { path } from '../../../../libs/path'
import { WalletGuardWallet } from '../../types'
import { WALLET_TYPE_LABELS } from '../config'
import {
  $finalWallets,
  $finalWalletsHasError,
  $paymentSystems,
  finalWalletsRequested,
  getFinalWalletsFx,
  walletNameFromAddress,
} from '../create/model'
import {
  editWalletFx,
  editWalletValidationFailed,
  walletEditSubmitted,
} from './model'
import { EditWalletValues } from './types'

type Props = {
  wallet: WalletGuardWallet
}

export const EditWalletForm = ({ wallet }: Props) => {
  const navigate = useNavigate()
  const type = wallet.type.toUpperCase()
  const isTransit = type === 'TRANSIT'
  const walletsPath = path.walletGuard.wallets()

  const { control, handleSubmit, setError } = useForm<EditWalletValues>({
    mode: 'onChange',
    defaultValues: {
      name: wallet.name,
      paymentSystems: wallet.paymentSystems,
      comment: wallet.comment ?? '',
      finalWalletId: wallet.finalWalletId,
    },
  })
  const {
    submitWallet,
    pending,
    finalWallets,
    loadingFinalWallets,
    finalWalletsHasError,
    requestFinalWallets,
    paymentSystems,
  } = useUnit({
    submitWallet: walletEditSubmitted,
    pending: editWalletFx.pending,
    finalWallets: $finalWallets,
    loadingFinalWallets: getFinalWalletsFx.pending,
    finalWalletsHasError: $finalWalletsHasError,
    requestFinalWallets: finalWalletsRequested,
    paymentSystems: $paymentSystems,
  })

  // Транзитный кошелёк можно привязать только к финальному с той же сетью и валютой.
  const compatibleFinalWallets = useMemo(
    () =>
      finalWallets.filter(
        (finalWallet) =>
          finalWallet.currency === wallet.currency &&
          finalWallet.network === wallet.network,
      ),
    [finalWallets, wallet.currency, wallet.network],
  )

  useEffect(() => {
    if (isTransit) requestFinalWallets()
  }, [isTransit, requestFinalWallets])

  // Состояние формы живёт в react-hook-form, поэтому результат запроса
  // применяем здесь: после успеха уходим на страницу кошелька, ошибки
  // сервера ставим на поля.
  useEffect(() => {
    const unwatchSaved = editWalletFx.done.watch(() => navigate(walletsPath))
    const unwatchValidation = editWalletValidationFailed.watch((items) =>
      items.forEach(({ field, msg }) =>
        setError(field as keyof EditWalletValues, { message: msg }),
      ),
    )
    return () => {
      unwatchSaved()
      unwatchValidation()
    }
  }, [navigate, walletsPath, setError])

  const renderFinalWalletsHint = () => {
    if (finalWalletsHasError) {
      return (
        <Typography.Text type="danger">
          Не удалось загрузить финальные кошельки.{' '}
          <Button
            type="link"
            size="small"
            onClick={() => requestFinalWallets()}
          >
            Повторить
          </Button>
        </Typography.Text>
      )
    }

    if (!loadingFinalWallets && !compatibleFinalWallets.length) {
      return `Нет финальных кошельков ${wallet.currency} в сети ${wallet.network}.`
    }

    return undefined
  }

  return (
    <Card>
      <Flex vertical gap={4} style={{ marginBottom: 20 }}>
        <Typography.Text copyable style={{ overflowWrap: 'anywhere' }}>
          {wallet.address}
        </Typography.Text>
        <Typography.Text type="secondary">
          {WALLET_TYPE_LABELS[type] ?? wallet.type} · {wallet.currency} ·{' '}
          {wallet.network}
        </Typography.Text>
      </Flex>
      <Form
        component={false}
        layout="vertical"
        size="large"
        variant="filled"
        disabled={pending}
      >
        <form
          name="edit-wallet"
          onSubmit={handleSubmit((values) => submitWallet({ wallet, values }))}
          noValidate
        >
          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label="Название / метка"
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                  >
                    <Input
                      {...field}
                      placeholder={walletNameFromAddress(wallet.address)}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Controller
                name="paymentSystems"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label="Платёжные системы"
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                  >
                    <Select
                      {...field}
                      mode="tags"
                      allowClear
                      placeholder="Добавить ПС…"
                      options={paymentSystems.map((value) => ({
                        value,
                        label: value,
                      }))}
                      tokenSeparators={[',']}
                      notFoundContent="Введите название платёжной системы"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            {isTransit && (
              <Col span={24}>
                <Controller
                  name="finalWalletId"
                  control={control}
                  rules={{
                    required: 'Выберите финальный кошелёк для транзитного',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      label="Финальный кошелёк"
                      required
                      validateStatus={error ? 'error' : undefined}
                      help={error?.message}
                      extra={renderFinalWalletsHint()}
                    >
                      <Select
                        {...field}
                        // При очистке Select отдаёт undefined, а react-hook-form
                        // на undefined подставляет значение по умолчанию, и поле
                        // снова показывает прежний кошелёк. Поэтому храним null.
                        value={field.value ?? undefined}
                        onChange={(value) => field.onChange(value ?? null)}
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        loading={loadingFinalWallets}
                        disabled={
                          pending || loadingFinalWallets || finalWalletsHasError
                        }
                        placeholder="Выберите финальный кошелёк"
                        notFoundContent={
                          loadingFinalWallets
                            ? 'Загрузка…'
                            : 'Финальные кошельки не найдены'
                        }
                        options={compatibleFinalWallets.map((finalWallet) => ({
                          value: finalWallet.id,
                          label: `${finalWallet.name} · ${finalWallet.currency} · ${finalWallet.address}`,
                        }))}
                      />
                    </Form.Item>
                  )}
                />
              </Col>
            )}
            <Col span={24}>
              <Controller
                name="comment"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label="Комментарий"
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                  >
                    <Input.TextArea
                      {...field}
                      placeholder="Комментарий к кошельку"
                      autoSize={{ minRows: 2, maxRows: 5 }}
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
          <Flex gap={8}>
            <Button type="primary" htmlType="submit" loading={pending}>
              Сохранить
            </Button>
            <Link to={walletsPath}>
              <Button>Отмена</Button>
            </Link>
          </Flex>
        </form>
      </Form>
    </Card>
  )
}
